import React, { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import logo from "assets/images/others/host-side-images/logo.png";
import { CreateBusinessDTO } from "utils/helpers/models/auth/business-register.dto";
import useAuth from "../useHooks";
import { useNavigate } from "react-router-dom";
import PhoneNumberInput from "components/phone-number-input";
import PlacesAutocomplete from "components/forms/places-autocomplete";
import FormErrorMessage from "components/forms/form-error-message";
import { INDUSTRY_TYPES, PROFILE_TYPES } from "utils/helpers/enums/shared.enum";
import useUtils from "hooks/useUtils";
import { ROLES } from "utils/helpers/enums/roles.enum";
import {
  HostUpgradeDTO,
  UpgradeProfileDTO,
} from "utils/helpers/models/auth/upgrade-profile.dto";
const RegisterInformation: React.FC = () => {
  const navigate = useNavigate();
  const { getQueryParams } = useUtils();
  const { upgrade = false } = getQueryParams();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<CreateBusinessDTO>(
    new CreateBusinessDTO()
  );

  const validatePassword = (password: string) => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push("at least 8 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("one lowercase letter");
    }
    if (!/\d/.test(password)) {
      errors.push("one number");
    }
    if (!/[\W_]/.test(password)) {
      errors.push("one special character");
    }
    // if (/\s/.test(password)) {
    //   errors.push("no spaces");
    // }

    return errors.length > 0
      ? `Password must include at least ${errors.join("/")}`
      : true;
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateBusinessDTO>();
  const { businessRegister, upgradeProfile } = useAuth();

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    // Capitalize first letter of each word for firstName, lastName, and businessName
    let processedValue = value;
    if (
      name === "firstName" ||
      name === "lastName" ||
      name === "businessName"
    ) {
      processedValue = value
        .split(" ")
        .map((word: string) =>
          word.length > 0
            ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            : word
        )
        .join(" ");
    }
    setValue(name as keyof CreateBusinessDTO, processedValue as any, {
      shouldValidate: true,
    });
  };

  const onChangeAddress = async (
    place: google.maps.places.PlaceResult & { description?: string }
  ) => {
    if (!place.address_components) return;

    // Extract address components
    const components = place.address_components || [];
    const getComp = (type: string) =>
      components.find((c) => c.types.includes(type))?.long_name || "";
    const getShortComp = (type: string) =>
      components.find((c) => c.types.includes(type))?.short_name || "";

    const city = getComp("locality") || getComp("sublocality") || "";
    const state = getComp("administrative_area_level_1"); // Full state name (e.g., "Indiana")
    const zipCode = getComp("postal_code");

    // Use the full description (what user sees) instead of abbreviated formatted_address
    formData.businessAddress = place?.description || place?.formatted_address;
    formData.placeId = place?.place_id;

    // Store extracted components (even if not in DTO, they'll be sent to backend)
    (formData as any).city = city;
    (formData as any).state = state;
    (formData as any).zipCode = zipCode;

    setValue("businessAddress", formData.businessAddress, {
      shouldValidate: true,
    });
    setValue("placeId", formData.placeId, { shouldValidate: true });
    setFormData({ ...formData });
  };

  useEffect(() => {
    register("placeId", {
      required: "Address is required",
    });
    register("businessAddress", {
      required: "Address is required",
    });
  }, [register]);

  const onSubmit = (data) => {
    console.log(data, "data");
    const dto = {
      ...data,
      userName: data.email,
      // Trim phone number before submission (matching mobile app behavior)
      businessPhoneNumber:
        data.businessPhoneNumber?.trim() || data.businessPhoneNumber,
    };

    if (upgrade) {
      const host = new HostUpgradeDTO(dto);
      const body = new UpgradeProfileDTO({
        profileType: PROFILE_TYPES.LEADER,
        role: ROLES.HOST,
        host,
      });
      upgradeProfile(body, true);
    } else {
      console.log(dto, "dto");
      businessRegister(dto);
    }
  };

  return (
    <div className="w-full h-screen overflow-y-auto flex flex-col p-4 sm:p-6 lg:px-8 xl:px-10 lg:py-6">
      {/* Logo */}
      <div className="flex justify-center my-6">
        <img src={logo} alt="logo" className="h-20" />
      </div>
      <div className="w-full flex flex-col gap-4">
        {/* Form Header */}
        <div className="w-full">
          <h1 className="text-3xl font-medium text-blackie">
            Business Information
          </h1>
          <p className="text-lg text-labelTextColor mt-2">
            Please enter your Basic Business information to get started with
            Learn Local.
          </p>
        </div>

        {/* Form Container */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3"
        >
          {!upgrade && (
            <Fragment>
              {/* First Name */}
              <div className="flex flex-col gap-1">
                <label className="custom-label">First Name</label>
                <input
                  type="text"
                  placeholder="Enter First Name"
                  {...register("firstName", {
                    required: "First Name is required",
                    maxLength: {
                      value: 30,
                      message: "First Name cannot exceed 30 characters",
                    },
                    validate: {
                      noNumbers: (value) =>
                        /^[^\d]+$/.test(value) ||
                        "First Name cannot contain numbers",
                      noLeadingTrailingSpaces: (value) =>
                        /^(?!\s)(?!.*\s$).+$/.test(value) ||
                        "First Name cannot start or end with a space",
                    },
                  })}
                  onChange={handleNameChange}
                  className="custom-input"
                />
                {errors.firstName && (
                  <span className="text-red-500 text-sm">
                    {errors.firstName.message}
                  </span>
                )}
              </div>

              {/* Last Name */}
              <div className="flex flex-col gap-1">
                <label className="custom-label">Last Name</label>
                <input
                  type="text"
                  placeholder="Enter Last Name"
                  {...register("lastName", {
                    required: "Last Name is required",
                    maxLength: {
                      value: 30,
                      message: "Last Name cannot exceed 30 characters",
                    },
                    validate: {
                      noNumbers: (value) =>
                        /^[^\d]+$/.test(value) ||
                        "Last Name cannot contain numbers",
                      noLeadingTrailingSpaces: (value) =>
                        /^(?!\s)(?!.*\s$).+$/.test(value) ||
                        "Last Name cannot start or end with a space",
                    },
                  })}
                  onChange={handleNameChange}
                  className="custom-input"
                />
                {errors.lastName && (
                  <span className="text-red-500 text-sm">
                    {errors.lastName.message}
                  </span>
                )}
              </div>
            </Fragment>
          )}

          {/* Business Name */}
          <div className="flex flex-col gap-1">
            <label className="custom-label">Business Name</label>
            <input
              type="text"
              placeholder="Enter Business Name"
              {...register("businessName", {
                required: "Business Name is required",
                maxLength: {
                  value: 50,
                  message: "Business Name cannot exceed 50 characters",
                },
                validate: {
                  notOnlyNumbers: (value) =>
                    !/^\d+$/.test(value) ||
                    "Business Name cannot be only numbers",
                },
              })}
              onChange={handleNameChange}
              className="custom-input"
            />
            {errors.businessName && (
              <span className="text-red-500 text-sm">
                {errors.businessName.message}
              </span>
            )}
          </div>

          {/* Business Email */}
          <div className="flex flex-col gap-1">
            <label className="custom-label">Business Email</label>
            <input
              type="email"
              placeholder="Enter Business Email"
              {...register("businessEmail", {
                required: "Business Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Invalid email format",
                },
              })}
              className="custom-input"
            />
            {errors.businessEmail && (
              <span className="text-red-500 text-sm">
                {errors.businessEmail.message}
              </span>
            )}
          </div>

          {/* Business Number */}
          <div className="flex flex-col ">
            <PhoneNumberInput
              control={control}
              name="businessPhoneNumber"
              label="Business Phone Number"
              error={errors.businessPhoneNumber}
              placeholder="Enter Business Phone Number"
              required={true}
            />
          </div>

          {/* Industry */}
          <div className="flex flex-col gap-1">
            <label className="custom-label">Industry</label>
            <select
              {...register("industry", { required: "Industry is required" })}
              className="custom-input"
            >
              <option className="h-10" value="">
                Select
              </option>
              {Object.values(INDUSTRY_TYPES).map((industry) => (
                <option
                  key={industry}
                  value={industry}
                  className="h-[40px]  text-labelTextColor"
                >
                  {industry.replace(/_/g, " ")}
                </option>
              ))}
            </select>
            {errors.industry && (
              <span className="text-red-500 text-sm">
                {errors.industry.message}
              </span>
            )}
          </div>

          {/* Address - Full Width */}
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label className="custom-label">Address</label>
            <PlacesAutocomplete
              value={formData.businessAddress}
              onChange={onChangeAddress}
            />
            {(errors.businessAddress || errors.placeId) && (
              <span className="text-red-500 text-sm">
                {errors.businessAddress?.message ||
                  errors.placeId?.message ||
                  "Address is required"}
              </span>
            )}
          </div>

          {!upgrade && (
            <Fragment>
              {/* Email */}
              <div className="flex flex-col gap-1">
                <label className="custom-label">Email</label>
                <input
                  type="email"
                  placeholder="Enter Email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Invalid email format",
                    },
                  })}
                  className="custom-input"
                />
                {errors.email && (
                  <span className="text-red-500 text-sm">
                    {errors.email.message}
                  </span>
                )}
              </div>

              {/* Password Input */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label className="custom-label">Password</label>
                  <div
                    className="cursor-pointer text-sm flex items-center text-label"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <>
                        <AiFillEyeInvisible size={18} className="mr-1" />
                        <span>Hide</span>
                      </>
                    ) : (
                      <>
                        <AiFillEye size={18} className="mr-1" />
                        <span>Show</span>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 8,
                        message: "Password must be at least 8 characters long",
                      },
                      validate: {
                        noLeadingTrailingSpaces: (value) =>
                          !/\s/.test(value) ||
                          "Password does not contain spaces.",
                        validatePassword,
                      },
                    })}
                    className="custom-input"
                  />
                </div>
                <div className="flex justify-between">
                  {errors.password && (
                    <span className="text-red-500 text-sm">
                      {errors.password.message}
                    </span>
                  )}
                </div>
              </div>
            </Fragment>
          )}

          {/* Submit Button */}
          <div className="flex justify-end mt-3 lg:mt-6 sm:col-span-2">
            <button
              type="submit"
              className="lg:h-[64px] px-6 py-3 mt-2 text-xl bg-primaryBlueBg rounded-full text-white w-fit lg:w-1/3"
            >
              {upgrade ? "Upgrade to Host" : "Get Started"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterInformation;
